import type { ICellRendererParams } from 'ag-grid-community'
import type { BadgeData } from '../utils/dom'
import { createTemplateRenderer } from '../utils/fast-renderer'
import { cloneBadgeNode, updateBadge } from './badge'

// Badges data type containing array of badge configurations
type BadgesData = {
  readonly badges: readonly BadgeData[]
}

/**
 * Container skeleton — parsed ONCE, cloned per cell. Badge children are
 * reconciled into it in `updateBadges` (a variable number per row).
 */
const BADGES_TEMPLATE = '<div class="flex gap-1"></div>'

/**
 * Reconcile the container's badge children to the data array and apply each
 * badge in place — append cloned badge nodes when more are needed, remove
 * extras when fewer. DOM-child diffing, never innerHTML, so the hot scroll path
 * never re-parses. Reuses the SINGLE badge skeleton + `updateBadge` from
 * badge.ts (one source of truth for a grid badge).
 */
const updateBadges = (
  container: HTMLElement,
  data: BadgesData | null | undefined,
  _params: ICellRendererParams
): void => {
  const badges = data?.badges ?? []

  while (container.childElementCount < badges.length) {
    container.appendChild(cloneBadgeNode())
  }

  while (container.childElementCount > badges.length) {
    container.removeChild(container.lastElementChild!)
  }

  badges.forEach((badge, index) => {
    updateBadge(container.children[index] as HTMLElement, badge)
  })
}

/**
 * Badges cell renderer — clones a pre-parsed container and reconciles its badge
 * children on refresh (no innerHTML re-parse on the hot scroll path).
 */
export const BadgesComponent = createTemplateRenderer<
  BadgesData | null | undefined
>(BADGES_TEMPLATE, updateBadges)

// Export types for external use
export type { BadgesData }
